import {
  Injectable,
  ComponentRef,
  ComponentFactoryResolver,
  Injector,
  ApplicationRef,
  EmbeddedViewRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { SubscriptionUpdateReason } from '../subscription/subscription.service';
import {
  MfAccountingServiceComponent,
  ServiceType,
} from '@web-frontend/microfrontends/mf-accounting-service/mf-accounting-service.component';

/**
 * Service to handle navigation and display of subscription plan views.
 * Encapsulates routing logic for opening subscription modals and pages.
 */
@Injectable({
  providedIn: 'root',
})
export class SubscriptionViewService {
  private modalComponentRef: ComponentRef<any> | null = null;

  constructor(
    private router: Router,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef
  ) {}

  /**
   * Opens the add seats modal as an overlay
   * @param fromUrl Optional URL to return to after closing the modal
   */
  openAddSeatsModal(fromUrl?: string): void {
    this.injectComponent({
      mode: ServiceType.addSeats,
      updateReason: SubscriptionUpdateReason.seats,
      fromUrl: fromUrl || window.location.pathname,
      openPlans: true,
    });
  }

  /**
   * Opens the onboarding plan modal as an overlay
   */
  openOnboardingPlanModal(): void {
    this.injectComponent({
      mode: ServiceType.onboardingPlan,
      fromUrl: window.location.pathname,
      openPlans: true,
    });
  }

  /**
   * Opens the onboarding plan modal as an overlay
   */
  openOnboardingDownloadInvoicesPlanModal(): void {
    this.injectComponent({
      mode: ServiceType.dowloadInvoicesPlan,
      fromUrl: window.location.pathname,
      openPlans: true,
    });
  }

  /**
   * Opens the regular plans page (full-page navigation)
   * @param updateReason Reason for the subscription update (seats or billing_documents)
   * @param fromUrl Optional URL to return to after closing
   */
  openRegularPlansPage(
    updateReason: SubscriptionUpdateReason,
    fromUrl?: string
  ): void {
    const params = [`updateReason=${updateReason}`];
    if (fromUrl) {
      params.push(`fromUrl=${fromUrl}`);
    }
    this.router.navigateByUrl(`admin/pricing?${params.join('&')}`);
  }

  /**
   * Closes the subscription plan modal
   */
  closePlanModal(): void {
    if (this.modalComponentRef) {
      this.appRef.detachView(this.modalComponentRef.hostView);
      this.modalComponentRef.destroy();
      this.modalComponentRef = null;
    }
  }

  /**
   * Injects a component into the DOM programmatically
   */
  private injectComponent(inputs: any): void {
    // Close existing modal if any
    this.closePlanModal();
    // Create component factory
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
      MfAccountingServiceComponent
    );

    // Create component
    this.modalComponentRef = componentFactory.create(this.injector);

    // Set inputs
    Object.keys(inputs).forEach((key) => {
      if (this.modalComponentRef) {
        this.modalComponentRef.instance[key] = inputs[key];
      }
    });

    // Attach to application
    this.appRef.attachView(this.modalComponentRef.hostView);

    // Get DOM element
    const domElem = (this.modalComponentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;

    // Append to body
    document.body.appendChild(domElem);
  }
}
